
import { defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import { fetchGhlPaymentCatalogList } from "@/api/ghlPayment.api";
import { getCurrentUser } from "@/utils/index";
import SeqNoticeCard from "@/components/dashboard/sequence/SeqNoticeCard.vue";

type ShopProduct = {
  name: string;
  amount: number;
  sku: string;
  imageUrl?: string;
  shareUrl?: string;
};

type SortField = "name" | "amount";
type SortDirection = "asc" | "desc";

export default defineComponent({
  name: "shopproducts",
  components: {
    SeqNoticeCard,
  },
  props: {
    widgetClasses: String,
  },
  data() {
    return {
      products: [] as ShopProduct[],
      loading: true,
      errorMessage: "",
      affiliateUsid: "",
      copiedSku: "",
      shareOpenSku: "",
      ccode: -1,
      searchQuery: "",
      sortField: "name" as SortField,
      sortDirection: "asc" as SortDirection,
      shareNetworks: [
        { network: "facebook", name: "Facebook", icon: "fab fa-facebook-f", color: "#1877f2" },
        { network: "messenger", name: "Messenger", icon: "fab fa-facebook-messenger", color: "#0084ff" },
        { network: "viber", name: "Viber", icon: "fab fa-viber", color: "#59267c" },
        { network: "whatsapp", name: "WhatsApp", icon: "fab fa-whatsapp", color: "#25d366" },
        { network: "telegram", name: "Telegram", icon: "fab fa-telegram-plane", color: "#0088cc" },
        { network: "twitter", name: "Twitter", icon: "fab fa-twitter", color: "#1da1f2" },
        { network: "email", name: "Email", icon: "far fa-envelope", color: "#333333" },
        { network: "sms", name: "SMS", icon: "far fa-comment-dots", color: "#333333" },
      ],
    };
  },
  computed: {
    filteredProducts(): ShopProduct[] {
      const query = String(this.searchQuery || "").trim().toLowerCase();
      let rows = this.products.slice();

      if (query) {
        rows = rows.filter((product) => {
          const name = String(product.name || "").toLowerCase();
          const sku = String(product.sku || "").toLowerCase();
          return name.indexOf(query) !== -1 || sku.indexOf(query) !== -1;
        });
      }

      rows.sort((a, b) => this.compareProducts(a, b));
      return rows;
    },
  },
  mounted() {
    this.ccode = Number(localStorage.getItem("ccode"));
    const currentUser = getCurrentUser();
    this.affiliateUsid =
      String(localStorage.getItem("ec_current_usid") || "").trim() ||
      String((currentUser && currentUser.usid) || "").trim();

    if (this.ccode === 0) {
      this.loadProducts();
    } else {
      this.loading = false;
    }
  },
  methods: {
    formatCurrency(amount: number) {
      return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
      }).format(Number(amount || 0));
    },

    buildAffiliateShareUrl(shareUrl?: string) {
      const baseUrl = String(shareUrl || "").trim();
      const usid = String(this.affiliateUsid || "").trim();
      if (!baseUrl || !usid) return baseUrl;
      const joiner = baseUrl.indexOf("?") !== -1 ? "&" : "?";
      return `${baseUrl}${joiner}ecpid=${encodeURIComponent(usid)}`;
    },

    shareTitle(product: ShopProduct) {
      return String(product.name || "").trim();
    },

    shareDescription(product: ShopProduct) {
      return `Get ${this.formatCurrency(product.amount)} — shop with my referral link.`;
    },

    toggleShare(sku: string) {
      this.shareOpenSku = this.shareOpenSku === sku ? "" : sku;
    },

    toggleSort(field: SortField) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }
      this.sortField = field;
      this.sortDirection = field === "amount" ? "desc" : "asc";
    },

    sortIconClass(field: SortField) {
      if (this.sortField !== field) {
        return "fas fa-sort text-muted ms-1";
      }
      return this.sortDirection === "asc"
        ? "fas fa-sort-up ms-1"
        : "fas fa-sort-down ms-1";
    },

    compareProducts(a: ShopProduct, b: ShopProduct) {
      const direction = this.sortDirection === "asc" ? 1 : -1;

      if (this.sortField === "amount") {
        const amountDiff = Number(a.amount || 0) - Number(b.amount || 0);
        if (amountDiff !== 0) {
          return amountDiff * direction;
        }
        return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        });
      }

      const nameDiff = String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      });
      if (nameDiff !== 0) {
        return nameDiff * direction;
      }

      return String(a.sku || "").localeCompare(String(b.sku || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    },

    async loadProducts() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchGhlPaymentCatalogList();
        if (!data || !data.success) {
          this.products = [];
          this.errorMessage = (data && (data.message || data.error)) || "Could not load products";
          return;
        }
        this.products = Array.isArray(data.products) ? data.products : [];
      } catch (err) {
        this.products = [];
        const errData = err && err.response && err.response.data;
        this.errorMessage =
          (errData && (errData.message || errData.error)) || (err && err.message) || "Could not load products";
      } finally {
        this.loading = false;
      }
    },

    async copyShareLink(product: { sku: string; shareUrl?: string }) {
      const url = this.buildAffiliateShareUrl(product.shareUrl);
      if (!url) return;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const input = document.createElement("input");
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        this.copiedSku = product.sku;
        setTimeout(() => {
          this.copiedSku = "";
        }, 2000);
      } catch (e) {
        console.error(e);
      }
    },
  },
  setup() {
    onMounted(() => {
      setCurrentPageTitle("Online Shop");
    });
  },
});
